import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UtensilsCrossed, BedDouble, MapPin, Check, Users, MessageCircle } from "lucide-react";

export default function PublicBooking() {
  const { slug } = useParams();
  const [biz, setBiz] = useState(undefined);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [checkin, setCheckin] = useState(new Date().toISOString().slice(0, 10));
  const [checkout, setCheckout] = useState(new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [avail, setAvail] = useState(null);
  const [sel, setSel] = useState(null); // time or room id
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", customer_email: "", party_size: 2, notes: "" });
  const [done, setDone] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/business/public/${slug}`).then((r) => setBiz(r.data)).catch(() => setBiz(null));
  }, [slug]);

  const loadAvail = useCallback(async () => {
    if (!biz) return;
    try {
      const params = biz.mode === "slot" ? { date } : { checkin, checkout };
      const { data } = await axios.get(`${API}/business/public/${slug}/availability`, { params });
      setAvail(data); setSel(null);
    } catch (e) { setAvail(null); }
  }, [biz, slug, date, checkin, checkout]);

  useEffect(() => { loadAvail(); }, [loadAvail]);

  const book = async () => {
    if (!form.customer_name || !form.customer_phone) { toast.error("Inserisci nome e telefono"); return; }
    if (!sel) { toast.error(biz.mode === "slot" ? "Scegli un orario" : "Scegli una camera"); return; }
    try {
      const payload = {
        ...form, party_size: Number(form.party_size),
        ...(biz.mode === "slot" ? { date, time: sel } : { checkin, checkout, resource_id: sel }),
      };
      const { data } = await axios.post(`${API}/public/${slug}/book`, payload);
      setDone(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore prenotazione"); }
  };

  if (biz === undefined) return <div className="min-h-screen grid place-items-center text-slate-400">Caricamento...</div>;
  if (biz === null) return <div className="min-h-screen grid place-items-center text-slate-500">Attività non trovata.</div>;

  const isSlot = biz.mode === "slot";
  const hero = biz.photo || "https://images.unsplash.com/photo-1783949482715-90a6894d62dd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

  if (done) {
    return (
      <div className="min-h-screen bg-background grid place-items-center p-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 max-w-md text-center shadow-sm">
          <div className="grid h-16 w-16 place-items-center rounded-full bg-emerald-100 text-emerald-600 mx-auto mb-4"><Check className="h-8 w-8" /></div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">{done.auto_confirmed ? "Prenotazione confermata!" : "Richiesta inviata!"}</h1>
          <p className="text-slate-500 mt-2">{done.auto_confirmed ? "La tua prenotazione è confermata." : "Riceverai una conferma appena l'attività la approva."}</p>
          <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2 text-sm text-emerald-700 justify-center">
            <MessageCircle className="h-4 w-4" /> Riceverai un messaggio WhatsApp
          </div>
          <p className="text-xs text-slate-400 mt-4">Powered by turismo.latina.it</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-center" richColors />
      <div className="relative h-56 sm:h-72">
        <img src={hero} alt={biz.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10" />
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-1 rounded-full bg-white/20 backdrop-blur px-3 py-1 text-xs font-medium mb-2">
            {isSlot ? <UtensilsCrossed className="h-3.5 w-3.5" /> : <BedDouble className="h-3.5 w-3.5" />} {biz.type}
          </span>
          <h1 className="font-heading text-3xl font-extrabold">{biz.name}</h1>
          {biz.address && <p className="flex items-center gap-1 text-white/90 text-sm mt-1"><MapPin className="h-4 w-4" /> {biz.address}</p>}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {biz.description && <p className="text-slate-600">{biz.description}</p>}

        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
          <h2 className="font-heading font-bold text-lg text-slate-900">Prenota il tuo {isSlot ? "tavolo" : "soggiorno"}</h2>

          {isSlot ? (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="public-booking-date" /></div>
              <div><Label>Persone</Label><Input type="number" min={1} value={form.party_size} onChange={(e) => set("party_size", e.target.value)} data-testid="public-booking-party" /></div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Check-in</Label><Input type="date" value={checkin} onChange={(e) => setCheckin(e.target.value)} data-testid="public-booking-checkin" /></div>
              <div><Label>Check-out</Label><Input type="date" value={checkout} onChange={(e) => setCheckout(e.target.value)} data-testid="public-booking-checkout" /></div>
              <div><Label>Ospiti</Label><Input type="number" min={1} value={form.party_size} onChange={(e) => set("party_size", e.target.value)} data-testid="public-booking-party" /></div>
            </div>
          )}

          <div>
            <Label>{isSlot ? "Scegli l'orario" : "Scegli la camera"}</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {!avail ? <p className="text-sm text-slate-400">Nessuna disponibilità.</p> :
                isSlot ? avail.slots.filter((s) => s.available > 0).map((s) => (
                  <button key={s.time} onClick={() => setSel(s.time)} data-testid={`public-slot-${s.time}`}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium ${sel === s.time ? "bg-primary text-white border-primary" : "border-slate-300 text-slate-700 hover:border-primary"}`}>
                    {s.time}
                  </button>
                )) : avail.rooms.filter((r) => r.available).map((r) => (
                  <button key={r.id} onClick={() => setSel(r.id)} data-testid={`public-room-${r.id}`}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium flex items-center gap-1 ${sel === r.id ? "bg-primary text-white border-primary" : "border-slate-300 text-slate-700 hover:border-primary"}`}>
                    {r.name} <span className="text-xs opacity-70"><Users className="h-3 w-3 inline" /> {r.capacity}</span>
                  </button>
                ))}
              {avail && ((isSlot && avail.slots.every((s) => s.available === 0)) || (!isSlot && avail.rooms.every((r) => !r.available))) &&
                <p className="text-sm text-destructive">Tutto esaurito per queste date.</p>}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
            <div><Label>Nome e cognome</Label><Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="public-name-input" /></div>
            <div><Label>Telefono (WhatsApp)</Label><Input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} placeholder="+39..." data-testid="public-phone-input" /></div>
          </div>
          <div><Label>Email (opzionale)</Label><Input value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} data-testid="public-email-input" /></div>
          <div><Label>Note</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Richieste speciali..." data-testid="public-notes-input" /></div>

          <Button onClick={book} className="w-full rounded-xl h-11" data-testid="public-book-btn">Invia prenotazione</Button>
          <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1"><MessageCircle className="h-3 w-3" /> Riceverai conferma su WhatsApp — Powered by turismo.latina.it</p>
        </div>
      </div>
    </div>
  );
}
